/**
 * ΜΟΝΑΔΙΚΗ πηγή αλήθειας για τα χρήματα του dine-in.
 *
 * Το ταμείο (DineInOperations) και το app σερβιτόρου είναι ΔΥΟ ξεχωριστές
 * οθόνες — αλλά ΔΕΝ επιτρέπεται να έχουν δύο υλοποιήσεις των υπολογισμών.
 * Κάθε φορά που είχαμε δύο αντίγραφα, απέκλιναν: το split payment εισέπραττε
 * προ-έκπτωσης ποσά στη μία οθόνη και μετά-έκπτωσης στην άλλη, και οι IRIS
 * πληρωμές επανέρχονταν ως «κάρτα».
 *
 * Ό,τι αφορά ποσά, εκπτώσεις, κεράσματα ή κατανομή πληρωμών μπαίνει ΕΔΩ.
 */

/** 'credit' = «Επί πιστώσει» (6/10/2026): καταγράφεται όπως τα μετρητά, μόνο ο τύπος αλλάζει. */
export type PaymentMethod = 'cash' | 'card' | 'iris' | 'credit';

export interface PaidItemRow {
  order_item_id: number;
  quantity_paid: number;
  payment_type: string;
}

export interface PayableUnit {
  orderId: number;
  itemId: number;
  /** 0-based θέση του τεμαχίου μέσα στη γραμμή (ποσότητα 3 → 0,1,2) */
  instanceIndex: number;
  name: string;
  size: string;
  notes: string;
  removedIngredients: any[];
  extraIngredients: any[];
  /** Τιμή ΜΕΤΑ την αναλογική έκπτωση session, στρογγυλεμένη στο λεπτό */
  price: number;
  quantity: 1;
}

export interface PaidCounts {
  cash: number;
  card: number;
  iris: number;
  credit: number;
}

const round2 = (n: number): number => parseFloat(n.toFixed(2));

/** Ποσό στο λεπτό, όπως παντού στο dine-in: parseFloat(n.toFixed(2)). */
export const roundToCents = round2;

const num = (v: any): number => {
  const n = parseFloat(String(v ?? 0));
  return Number.isFinite(n) ? n : 0;
};

/* ───────────────────────── Τιμή γραμμής παραγγελίας ───────────────────────── */

/** Ό,τι χρειάζεται από μια επιλογή (selected_options: { group_id, …, price_modifier }). */
export interface PricedOption {
  price_modifier?: number | string | null;
}

/** Ό,τι χρειάζεται από ένα έξτρα υλικό (addedExtras: { name, quantity, price }). */
export interface PricedExtra {
  price: number | string;
  quantity: number | string;
}

/** Σ price_modifier των επιλογών, με τη σειρά τους (ίδια πρόσθεση με τις οθόνες ως τώρα). */
export function optionsTotal(options: PricedOption[] | null | undefined): number {
  return (options || []).reduce((s: number, o) => s + (Number(o?.price_modifier) || 0), 0);
}

/** Σ τιμή × ποσότητα των έξτρα, με τη σειρά τους. */
export function extrasTotal(extras: PricedExtra[] | null | undefined): number {
  return (extras || []).reduce((s: number, e) => s + Number(e?.price) * Number(e?.quantity), 0);
}

/**
 * Τιμή μονάδας γραμμής dine-in ΠΡΙΝ από την έκπτωση γραμμής:
 *
 *     βάση + Σ επιλογών + Σ έξτρα
 *
 * `base` = ό,τι χρεώνει το είδος χωρίς προσθήκες, όπως το βρίσκει η κάθε οθόνη (τιμή μεγέθους,
 * «Κανονικό», ζύγισμα/ανοιχτή τιμή στην επεξεργασία γραμμής — utils/basketLineEdit). Η ΙΔΙΑ
 * συνάρτηση για την οθόνη τραπεζιών του ταμία (DineInOperations: γρήγορη προσθήκη, προσθήκη με
 * μέγεθος, modal προσαρμογής) ΚΑΙ για την εφαρμογή σερβιτόρου (OrderScreen): μία προσαύξηση
 * επιλογής δεν επιτρέπεται να μετρά αλλιώς στο κινητό και αλλιώς στο ταμείο.
 *
 * ΧΩΡΙΣ στρογγύλευση, ίδια πρόσθεση με πριν (βάση, μετά επιλογές, μετά έξτρα): ο ταμίας στέλνει
 * τη γρήγορη προσθήκη ως έχει και στρογγυλεύει στο λεπτό ΜΕΤΑ την έκπτωση γραμμής· ο σερβιτόρος
 * στρογγυλεύει αμέσως (roundToCents), όπως έκανε ήδη για την τιμή μεγέθους. Αριθμοί-κείμενο
 * (NUMERIC του pg) μετρούν ως αριθμοί, όχι ως συνένωση συμβολοσειρών.
 */
export function unitPriceWithAddOns(
  base: number,
  options?: PricedOption[] | null,
  extras?: PricedExtra[] | null
): number {
  return base + optionsTotal(options) + extrasTotal(extras);
}

const parseList = (v: any): any[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Τα items που ΠΛΗΡΩΝΟΝΤΑΙ. Το endpoint λεπτομερειών τραπεζιού επιστρέφει τα
 * πάντα — ακυρωμένες παραγγελίες, κεράσματα και μετακινημένα items — και αν
 * δεν φιλτραριστούν εμφανίζονται ως χρεώσιμα στο split.
 */
export function selectPayableEntries(orders: any[]): Array<{ order: any; item: any }> {
  const out: Array<{ order: any; item: any }> = [];
  (orders || []).forEach((order: any) => {
    if (order?.status === 'cancelled') return;
    (order?.items || []).forEach((item: any) => {
      if (item?.is_treat) return;                                  // κέρασμα = μηδενική χρέωση
      if (item?.status === 'cancelled' || item?.status === 'moved') return;
      out.push({ order, item });
    });
  });
  return out;
}

/**
 * Αναλογία έκπτωσης session.
 *
 * ΠΡΟΣΟΧΗ: το `session.total_amount` είναι ΗΔΗ μετά την έκπτωση
 * (total_amount = subtotal - discount_amount, βλ. discount endpoint), ενώ τα
 * items κρατούν προ-έκπτωσης τιμές. Άρα ο σωστός λόγος είναι
 *
 *     ratio = session.total_amount / Σ(item.total_price)
 *
 * και ΟΧΙ (total - discount)/total — αυτό θα αφαιρούσε την έκπτωση δεύτερη φορά.
 */
export function computeDiscountRatio(sessionTotal: any, itemsSum: number): number {
  if (itemsSum <= 0 || !hasAmount(sessionTotal)) return 1;   // άγνωστο σύνολο → τιμές ως έχουν
  const total = num(sessionTotal);
  // Σύνολο 0 € (έκπτωση 100%): ΟΛΑ μηδέν. Πριν έπεφτε στο «1» και η πληρωμή ζητούσε όλο το
  // ποσό (GENERIS Π1, 5/10/2026).
  if (total <= 0) return 0;
  if (total >= itemsSum) return 1;      // καμία έκπτωση (ή ασυμφωνία) → μην πειράξεις τιμές
  return total / itemsSum;
}

/** Υπάρχει πραγματικό ποσό (και 0) — όχι null/undefined/κενό, που σημαίνουν «άγνωστο». */
function hasAmount(v: any): boolean {
  if (v === null || v === undefined || String(v).trim() === '') return false;
  return Number.isFinite(parseFloat(String(v)));
}

/**
 * Σπάει τις γραμμές σε μεμονωμένα τεμάχια με τιμή μετά την έκπτωση, ώστε ο
 * χρήστης να επιλέγει «αυτή η μπύρα με μετρητά, εκείνη με κάρτα».
 */
export function flattenPayableUnits(orders: any[], sessionTotal: any): PayableUnit[] {
  const entries = selectPayableEntries(orders);
  const itemsSum = entries.reduce((s, { item }) => s + num(item.total_price), 0);
  const ratio = computeDiscountRatio(sessionTotal, itemsSum);

  const units: PayableUnit[] = [];
  entries.forEach(({ order, item }) => {
    const qty = Math.max(1, parseInt(String(item.quantity ?? 1), 10) || 1);
    const price = round2((num(item.total_price) / qty) * ratio);
    for (let i = 0; i < qty; i++) {
      units.push({
        orderId: order.id,
        itemId: item.id,
        instanceIndex: i,
        name: item.product_name || item.name || '',
        size: item.size_name || item.size || '',
        notes: item.notes || '',
        removedIngredients: parseList(item.removed_ingredients),
        extraIngredients: parseList(item.extras ?? item.extra_ingredients),
        price,
        quantity: 1
      });
    }
  });
  return units;
}

/** Σύνολο λογαριασμού όπως θα πληρωθεί (μετά έκπτωση, χωρίς κεράσματα). */
export function payableTotal(orders: any[], sessionTotal: any): number {
  return round2(flattenPayableUnits(orders, sessionTotal).reduce((s, u) => s + u.price, 0));
}

export function sumUnits(units: Array<{ price: number; quantity: number }>): number {
  return round2(units.reduce((s, u) => s + u.price * u.quantity, 0));
}

/** Ο τύπος πληρωμής του server → μέθοδος. Το «Επί πιστώσει» έχει δική του (ΠΟΤΕ «κάρτα»)·
 *  κάθε άλλος άγνωστος τύπος μετρά ως κάρτα, όπως πριν. */
export function paymentMethodOf(paymentType: string | null | undefined): PaymentMethod {
  if (paymentType === 'cash') return 'cash';
  if (paymentType === 'iris') return 'iris';
  if (paymentType === 'credit') return 'credit';
  return 'card';
}

/** Όλα τα πληρωμένα τεμάχια, όποια κι αν είναι η μέθοδος. */
const allPaidUnits = (paid: Record<PaymentMethod, PayableUnit[]>): PayableUnit[] =>
  [...paid.cash, ...paid.card, ...paid.iris, ...paid.credit];

/** Πληρωμένα τεμάχια ανά item ΚΑΙ ανά μέθοδο (ποτέ «πρώτη μέθοδος για όλα»). */
export function bucketPaidItems(paidItems: PaidItemRow[]): Record<number, PaidCounts> {
  const map: Record<number, PaidCounts> = {};
  (paidItems || []).forEach((p) => {
    if (!map[p.order_item_id]) map[p.order_item_id] = { cash: 0, card: 0, iris: 0, credit: 0 };
    map[p.order_item_id][paymentMethodOf(p.payment_type)] += p.quantity_paid || 1;
  });
  return map;
}

/**
 * Ποια τεμάχια είναι ήδη πληρωμένα και με τι. Η σειρά cash → card → iris → credit είναι
 * αυθαίρετη αλλά ΠΡΕΠΕΙ να είναι σταθερή: ο server δεν καταγράφει ποιο
 * συγκεκριμένο τεμάχιο πληρώθηκε με ποια μέθοδο, μόνο πλήθη ανά μέθοδο.
 */
export function splitPaidVsUnpaid(
  units: PayableUnit[],
  paidCounts: Record<number, PaidCounts>
): { paid: Record<PaymentMethod, PayableUnit[]>; unpaid: PayableUnit[] } {
  const paid: Record<PaymentMethod, PayableUnit[]> = { cash: [], card: [], iris: [], credit: [] };
  const unpaid: PayableUnit[] = [];

  units.forEach((unit) => {
    const counts = paidCounts[unit.itemId] || { cash: 0, card: 0, iris: 0, credit: 0 };
    const i = unit.instanceIndex;
    if (i < counts.cash) paid.cash.push(unit);
    else if (i < counts.cash + counts.card) paid.card.push(unit);
    else if (i < counts.cash + counts.card + counts.iris) paid.iris.push(unit);
    else if (i < counts.cash + counts.card + counts.iris + counts.credit) paid.credit.push(unit);
    else unpaid.push(unit);
  });

  return { paid, unpaid };
}

export const MONEY_TOLERANCE = 0.005;

/**
 * Πόσα έχουν ΗΔΗ εισπραχθεί από το τραπέζι.
 *
 * Το πραγματικό ποσό: `session.paid_amount` του GET …/tables/:id/details = Σ των ΜΗ ακυρωμένων
 * πληρωμών (dine_in_payments.amount). Το ΙΔΙΟ άθροισμα κρατά και ο server: φρουρός ποσού
 * (utils/dineInPaymentGuard), process-payment, κλείσιμο με 409 unpaid_balance.
 *
 * ΟΧΙ οι τιμές των πληρωμένων τεμαχίων: αυτές ξαναϋπολογίζονται με τη ΣΗΜΕΡΙΝΗ αναλογία έκπτωσης.
 * Αν η έκπτωση ή τα είδη αλλάξουν ανάμεσα σε δύο δόσεις (ανοιχτή συνεδρία: εκπαίδευση, κατάστημα
 * χωρίς πάροχο) ή αν μια δόση κόπηκε στο υπόλοιπο, τα τεμάχια λένε άλλο ποσό από ό,τι πληρώθηκε
 * και η τελευταία δόση θα έβγαζε Σ πληρωμών ≠ σύνολο τραπεζιού.
 *
 * Μόνο όταν ο server δεν στέλνει το πεδίο (παλιό backend): οι τιμές των πληρωμένων τεμαχίων,
 * όπως μετρούσε ως τώρα το PDA — ίδιο αποτέλεσμα στη συνήθη ροή.
 */
export function alreadyPaidAmount(session: any, paidUnits: PayableUnit[]): number {
  const actual = session?.paid_amount;
  if (hasAmount(actual)) return round2(Math.max(0, num(actual)));
  return sumUnits(paidUnits);
}

/** Η εικόνα πληρωμής ενός τραπεζιού — ΙΔΙΑ για ταμείο (DineInOperations) και PDA (PaymentScreen). */
export interface TablePaymentState {
  /** Όλα τα χρεώσιμα τεμάχια, με τιμή μετά την έκπτωση λογαριασμού. */
  units: PayableUnit[];
  /** Τα πληρωμένα τεμάχια ανά μέθοδο (βλ. splitPaidVsUnpaid). */
  paid: Record<PaymentMethod, PayableUnit[]>;
  unpaid: PayableUnit[];
  /** Το σύνολο τραπεζιού (session.total_amount)· άγνωστο → Σ τεμαχίων, όπως η computeDiscountRatio. */
  total: number;
  /** Ήδη εισπραγμένα (alreadyPaidAmount). */
  alreadyPaid: number;
  /** total − alreadyPaid, ≥ 0: ό,τι χρεώνει η τελευταία δόση. */
  remainder: number;
}

/**
 * Από τα στοιχεία τραπεζιού (GET …/details: session, orders, paidItems) στο τι μένει να πληρωθεί.
 * Το ποσό κάθε δόσης βγαίνει από εδώ ΜΕ την sliceAmount(επιλεγμένα, unpaid, total, alreadyPaid).
 */
export function tablePaymentState(
  details: { session?: any; orders?: any[] | null; paidItems?: PaidItemRow[] | null } | null | undefined
): TablePaymentState {
  const session = details?.session;
  const units = flattenPayableUnits(details?.orders || [], session?.total_amount);
  const { paid, unpaid } = splitPaidVsUnpaid(units, bucketPaidItems(details?.paidItems || []));
  const total = hasAmount(session?.total_amount)
    ? round2(Math.max(0, num(session.total_amount)))
    : sumUnits(units);
  // + η προκαταβολή κράτησης που θα γραφτεί με την πρώτη πληρωμή (κατάστημα χωρίς πάροχο): ο server τη
  // γράφει πριν από τον φρουρό ποσού, άρα η τελευταία δόση πρέπει να τη λογαριάζει ήδη.
  const alreadyPaid = round2(alreadyPaidAmount(session, allPaidUnits(paid)) + Math.max(0, num(session?.deposit_pending)));
  return { units, paid, unpaid, total, alreadyPaid, remainder: round2(Math.max(0, total - alreadyPaid)) };
}

/**
 * Το payload `items` για cash-payment / card-payment.
 *
 * ΚΡΙΣΙΜΟ: ο server (recordPaymentItems) διαβάζει `quantity` και, αν λείπει,
 * πέφτει πίσω στη ΣΥΝΟΛΙΚΗ ποσότητα της γραμμής. Στέλνοντας μία εγγραφή ανά
 * τεμάχιο χωρίς quantity — όπως έκανε ο παλιός κώδικας — 2 από 3 μπύρες
 * καταγράφονταν ως 6 πληρωμένα τεμάχια: οι υπόλοιπες έμεναν «πληρωμένες»
 * χωρίς να εισπραχθούν και η σέσια έκλεινε νωρίς.
 *
 * Σωστό: μία εγγραφή ανά itemId με ρητό πλήθος και τιμή μονάδας.
 */
export function aggregatePaymentItems(
  units: PayableUnit[]
): Array<{ orderId: number; itemId: number; quantity: number; price: number }> {
  const byItem = new Map<number, { orderId: number; itemId: number; quantity: number; price: number }>();
  units.forEach((u) => {
    const existing = byItem.get(u.itemId);
    if (existing) existing.quantity += 1;
    else byItem.set(u.itemId, { orderId: u.orderId, itemId: u.itemId, quantity: 1, price: u.price });
  });
  return Array.from(byItem.values());
}

/**
 * Πόσο να χρεωθεί αυτή η δόση — ΚΑΙ στο ταμείο ΚΑΙ στο PDA («Ένα νούμερο παντού», 5/10/2026).
 *
 * Τα endpoints split πληρωμής ΔΕΝ ελέγχουν το ποσό μιας πληρωμής με είδη — ο client είναι ο μόνος
 * φύλακας. Επιπλέον, επειδή κάθε τιμή μονάδας στρογγυλοποιείται ξεχωριστά, το άθροισμα μπορεί να
 * διαφέρει λίγα λεπτά από το `session.total_amount` (GENERIS τραπέζι 52, έκπτωση 25%: σύνολο 59,47,
 * τεμάχια 59,48). Άρα η ΤΕΛΕΥΤΑΙΑ δόση χρεώνεται ως «υπόλοιπο», όχι ως άθροισμα τιμών, και καμία
 * ενδιάμεση δεν το ξεπερνά — αλλιώς ο πελάτης χρεώνεται άλλο ποσό από την απόδειξη.
 *
 * `total` και `alreadyPaid`: από την tablePaymentState (ταμείο: ό,τι ίσχυε όταν άνοιξε η οθόνη
 * + ό,τι εισπράχθηκε από τότε). Αποτέλεσμα ≤ 0,005 = τίποτα να εισπραχθεί: οι οθόνες ΔΕΝ στέλνουν.
 */
export function sliceAmount(
  selectedUnits: PayableUnit[],
  allUnpaidUnits: PayableUnit[],
  total: any,
  alreadyPaid: number
): number {
  const remainder = round2(Math.max(0, num(total) - alreadyPaid));
  const isFinalSlice = selectedUnits.length >= allUnpaidUnits.length;
  if (isFinalSlice) return remainder;
  return Math.min(sumUnits(selectedUnits), remainder);
}

export interface BillLine {
  name: string;
  size: string | null;
  quantity: number;
  /** Ποσό γραμμής ΠΡΙΝ την έκπτωση λογαριασμού (όπως στο μενού). */
  amount: number;
  treat: boolean;
}

export interface BillSummary {
  lines: BillLine[];
  /** Σ ειδών πριν την έκπτωση (χωρίς κεράσματα). */
  subtotal: number;
  discount: number;
  /** Ό,τι θα χρεώσει η απόδειξη — ίδιο με το session.total_amount. */
  total: number;
  paid: number;
  remaining: number;
}

/**
 * Ο λογαριασμός που πηγαίνει στο τραπέζι πριν την απόδειξη (κουμπί «Λογαριασμός»
 * σε ταμείο και PDA). ΙΔΙΟΙ κανόνες με την πληρωμή: ίδια χρεώσιμα είδη, ίδια
 * έκπτωση, ίδιο υπόλοιπο — αλλιώς ο πελάτης βλέπει άλλο ποσό στο χαρτί κι άλλο στο POS.
 *
 * Οι γραμμές ενώνονται ανά είδος/μέγεθος/τιμή (δύο καφέδες σε δύο γύρους = «2 x»).
 * Τα κεράσματα φαίνονται ως γραμμή «ΚΕΡΑΣΜΑ» χωρίς ποσό.
 */
export function buildBillSummary(orders: any[], sessionTotal: any, paidItems: PaidItemRow[] = []): BillSummary {
  const lines = new Map<string, BillLine>();
  const addLine = (item: any, treat: boolean) => {
    const qty = Math.max(1, parseInt(String(item.quantity ?? 1), 10) || 1);
    const amount = treat ? 0 : num(item.total_price);
    const name = item.product_name || item.name || '';
    const size = item.size_name || item.size || null;
    const unitCents = Math.round((amount / qty) * 100);
    const key = `${treat ? 'T' : 'P'}|${name}|${size ?? ''}|${unitCents}`;
    const existing = lines.get(key);
    if (existing) {
      existing.quantity += qty;
      existing.amount = round2(existing.amount + amount);
    } else {
      lines.set(key, { name, size, quantity: qty, amount: round2(amount), treat });
    }
  };

  selectPayableEntries(orders).forEach(({ item }) => addLine(item, false));
  (orders || []).forEach((order: any) => {
    if (order?.status === 'cancelled') return;
    (order?.items || []).forEach((item: any) => {
      if (item?.is_treat && item?.status !== 'cancelled' && item?.status !== 'moved') addLine(item, true);
    });
  });

  const subtotal = round2(selectPayableEntries(orders).reduce((s, { item }) => s + num(item.total_price), 0));
  // Ο ίδιος κανόνας με την computeDiscountRatio: σύνολο λογαριασμού μέσα [0, subtotal] = μετά την
  // έκπτωση (και 0 € στο 100%)· άγνωστο σύνολο → οι τιμές των ειδών.
  const sessionAmount = num(sessionTotal);
  const total = subtotal > 0 && hasAmount(sessionTotal) && sessionAmount >= 0 && sessionAmount <= subtotal
    ? round2(sessionAmount)
    : subtotal;

  const units = flattenPayableUnits(orders, sessionTotal);
  const { paid } = splitPaidVsUnpaid(units, bucketPaidItems(paidItems));
  const paidAmount = Math.min(total, sumUnits(allPaidUnits(paid)));

  return {
    lines: Array.from(lines.values()),
    subtotal,
    discount: round2(Math.max(0, subtotal - total)),
    total,
    paid: round2(paidAmount),
    remaining: round2(Math.max(0, total - paidAmount))
  };
}
